import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { formatDistanceToNow } from "date-fns";

import { faPaperclip } from "@fortawesome/pro-regular-svg-icons";

import { Ticket } from "@signalist/shared-types";

import { Icon } from "@/components/Icon/Icon";
import { Table, TableColumn } from "@/components/Table/Table";
import { ROUTE_CONFIG } from "@/routes/config";

import { SupportTicketsController } from "../hooks/useSupportTickets";
import { lastTicketActivity, ticketFilerName, ticketLinkedEntityKey } from "../utils/ticketRowMeta";
import { TicketCategoryPill, TicketStatusPill } from "./TicketPills";

/**
 * The customer's ticket rows (mockup `signalist-tickets-customer-v1.html`). The whole filtered set
 * is on screen — the page fetches one window of tickets and filters it client-side — so the table
 * carries no pagination of its own.
 */
export const SupportTicketsTable: FunctionComponent<{
	controller: SupportTicketsController;
	onNewTicket: () => void;
}> = ({ controller, onNewTicket }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const columns: TableColumn[] = [
		{
			title: t("support.columns.ticket"),
			width: "110px",
			render: (ticket: Ticket) => (
				<span className="text-sm tabular-nums text-text-meta">#{ticket.id}</span>
			),
		},
		{
			title: t("support.columns.subject"),
			render: (ticket: Ticket) => {
				const linked = ticketLinkedEntityKey(ticket);

				return (
					<div className="flex min-w-0 flex-col gap-0.5">
						<span className="truncate text-sm font-semibold text-text">{ticket.subject}</span>
						<span className="truncate text-xs text-text-meta">
							{[
								ticketFilerName(ticket),
								linked ? t(`support.linkedEntity.${linked.key}`, { id: linked.id }) : null,
							]
								.filter(Boolean)
								.join(" · ")}
						</span>
					</div>
				);
			},
		},
		{
			title: t("support.columns.category"),
			width: "150px",
			render: (ticket: Ticket) => <TicketCategoryPill category={ticket.category} />,
		},
		{
			title: t("support.columns.status"),
			width: "170px",
			render: (ticket: Ticket) => <TicketStatusPill status={ticket.status} />,
		},
		{
			title: t("support.columns.attachments"),
			width: "80px",
			render: (ticket: Ticket) => {
				// A SQL sub-query count: it arrives as a string (web CLAUDE.md §6).
				const count = Number(ticket.attachmentCount ?? 0);

				return count > 0 ? (
					<span className="inline-flex items-center gap-1 text-xs text-text-meta">
						<Icon icon={faPaperclip} />
						{count}
					</span>
				) : (
					<span />
				);
			},
		},
		{
			title: t("support.columns.lastActivity"),
			width: "160px",
			render: (ticket: Ticket) => {
				const activity = lastTicketActivity(ticket);

				return (
					<div className="flex flex-col">
						<span className="whitespace-nowrap text-sm text-text">
							{formatDistanceToNow(new Date(activity.at), { addSuffix: true })}
						</span>
						<span className="text-[11px] text-text-meta">
							{t(`support.activity.${activity.kind}`)}
						</span>
					</div>
				);
			},
		},
	];

	return (
		<div className="flex min-h-0 flex-1 flex-col">
			<Table
				columns={columns}
				data={controller.rows}
				hidePagination
				isLoading={controller.isLoading}
				noDataCta={
					controller.hasAnyTicket
						? undefined
						: { title: t("support.newTicket"), action: onNewTicket }
				}
				noDataText={
					controller.hasAnyTicket ? (
						t("support.empty.filtered")
					) : (
						<span className="flex flex-col items-center gap-1">
							<span className="text-sm font-semibold text-text">{t("support.empty.title")}</span>
							<span className="max-w-[420px] text-sm text-text-meta">
								{t("support.empty.body")}
							</span>
						</span>
					)
				}
				// The detail page lands with PREC-3938; until then this route has no element.
				handleRowClick={(id: number) => navigate(`${ROUTE_CONFIG.SUPPORT}/${id}`)}
			/>

			{controller.isTruncated && (
				<span className="px-4 py-2 text-xs text-text-meta">{t("support.truncated")}</span>
			)}
		</div>
	);
};
